import React, { useEffect, useRef, useState } from "react";
import { Terminal as TerminalIcon } from "lucide-react";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { getWebContainer } from "../../services/webcontainer";

interface TerminalProps {
  height?: number;
  onHeightChange?: (height: number) => void;
  isResizable?: boolean;
  /** When false, render only the terminal surface (the dock supplies the chrome). */
  showHeader?: boolean;
  /** When true, fill the parent height instead of using the fixed `height` prop. */
  fill?: boolean;
  // Kept for backwards-compatible call sites. Mounting the file system is owned
  // by the workspace; this terminal just attaches an interactive shell to the
  // already-booted WebContainer, so these props are intentionally unused.
  workingDirectory?: string;
  fileSystem?: { [path: string]: string };
  packageJson?: any;
  onFileSystemChange?: (newFileSystem: { [path: string]: string }) => void;
  className?: string;
}

/**
 * Real terminal emulator (xterm.js) wired to WebContainer's interactive `jsh`
 * shell. xterm interprets ANSI escape sequences, so colors, spinners, `clear`,
 * arrow-key history and cursor movement all work — unlike the previous hand-
 * rolled <pre> renderer that printed control codes (`[2J`, `[91m`, …) as text.
 */
const Terminal: React.FC<TerminalProps> = ({
  height = 256,
  onHeightChange,
  isResizable = false,
  showHeader = true,
  fill = false,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const termRef = useRef<XTerm | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const shellRef = useRef<any>(null);
  const writerRef = useRef<WritableStreamDefaultWriter<string> | null>(null);
  const [status, setStatus] = useState<"booting" | "ready" | "error">("booting");
  const [isResizing, setIsResizing] = useState(false);

  // Boot xterm + the jsh shell exactly once per mount.
  useEffect(() => {
    let disposed = false;

    const boot = async () => {
      if (!hostRef.current) return;

      const term = new XTerm({
        cursorBlink: true,
        fontSize: 13,
        fontFamily: 'Menlo, Monaco, "Courier New", monospace',
        theme: { background: "#1e1e1e", foreground: "#d4d4d4" },
        convertEol: true,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(hostRef.current);
      try { fit.fit(); } catch { /* container not sized yet */ }
      termRef.current = term;
      fitRef.current = fit;

      try {
        const wc = await getWebContainer();
        if (disposed) return;

        const shell = await wc.spawn("jsh", {
          terminal: { cols: term.cols || 80, rows: term.rows || 24 },
        });
        shellRef.current = shell;

        // shell stdout/stderr -> xterm
        shell.output.pipeTo(
          new WritableStream({
            write(data: string) {
              term.write(data);
            },
          }),
        ).catch(() => { /* stream closed on teardown */ });

        // xterm keystrokes -> shell stdin
        const writer = shell.input.getWriter();
        writerRef.current = writer;
        term.onData((data: string) => {
          writer.write(data).catch(() => { });
        });

        // keep the PTY size in sync with xterm
        term.onResize(({ cols, rows }: { cols: number; rows: number }) => {
          try { shell.resize({ cols, rows }); } catch { /* noop */ }
        });

        setStatus("ready");
        setTimeout(() => { try { term.focus(); } catch { /* noop */ } }, 50);
      } catch (e: any) {
        term.write(`\r\n\x1b[31mFailed to start shell: ${e?.message || e}\x1b[0m\r\n`);
        setStatus("error");
      }
    };

    boot();

    return () => {
      disposed = true;
      try { writerRef.current?.releaseLock(); } catch { /* noop */ }
      try { shellRef.current?.kill(); } catch { /* noop */ }
      try { termRef.current?.dispose(); } catch { /* noop */ }
      termRef.current = null;
      shellRef.current = null;
      writerRef.current = null;
    };
  }, []);

  // Refit whenever the panel height changes.
  useEffect(() => {
    const id = setTimeout(() => {
      try { fitRef.current?.fit(); } catch { /* noop */ }
    }, 60);
    return () => clearTimeout(id);
  }, [height]);

  // Refit on any container size change (window resize, editor split changes).
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(() => {
      try { fitRef.current?.fit(); } catch { /* noop */ }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Drag-to-resize. The terminal sits at the viewport bottom, so its height is
  // (viewport bottom - pointer Y), clamped so the header can never be hidden.
  useEffect(() => {
    if (!isResizable || !isResizing) return;
    const onMove = (e: MouseEvent) => {
      const next = window.innerHeight - e.clientY;
      const clamped = Math.max(120, Math.min(window.innerHeight - 220, next));
      onHeightChange?.(clamped);
    };
    const onUp = () => setIsResizing(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isResizing, onHeightChange, isResizable]);

  return (
    <div
      ref={containerRef}
      className={`relative bg-[#1e1e1e] flex flex-col overflow-hidden ${fill ? "h-full" : ""} ${className}`}
      style={fill ? undefined : { height: `${height}px` }}
    >
      {/* Header doubles as the drag handle (VS Code style) */}
      {showHeader && (
        <div
          onMouseDown={() => isResizable && setIsResizing(true)}
          className={`h-9 flex items-center px-3 bg-[#2d2d30] border-b border-gray-700 flex-shrink-0 select-none ${isResizable ? "cursor-ns-resize" : ""
            }`}
          title={isResizable ? "Drag to resize" : undefined}
        >
          {isResizable && (
            <span className="mr-2 text-gray-500 leading-none tracking-widest">⋯</span>
          )}
          <TerminalIcon className="w-4 h-4 text-gray-400 mr-2" />
          <span className="text-sm text-gray-300">Terminal</span>

          <span className="ml-3 text-xs">
            {status === "booting" && <span className="text-yellow-400 animate-pulse">⏳ Booting</span>}
            {status === "ready" && <span className="text-green-400">✓ Ready</span>}
            {status === "error" && <span className="text-red-400">✗ Error</span>}
          </span>

          <button
            onClick={(e) => {
              e.stopPropagation();
              termRef.current?.clear();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="ml-auto text-xs text-gray-400 hover:text-white px-2 py-0.5 rounded hover:bg-white/10 transition-colors"
            title="Clear terminal"
          >
            Clear
          </button>
        </div>
      )}

      <div
        ref={hostRef}
        onClick={() => termRef.current?.focus()}
        className="flex-1 min-h-0 relative overflow-hidden px-2 py-1"
      />
    </div>
  );
};

export default Terminal;
